import { useMemo, useState } from "react"
import {
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import { Userbot } from "../../interfaces"
import { useLazyClientsQuery, useLazyRolesQuery } from "../../store"
import { useInfiniteSelectList } from "@/core/hooks"
import { infiniteListboxProps } from "@/core/components"
import { massOpsRunner } from "./massOpsRunnerBridge"
import type { MassOpItem } from "./massOpsRunner"
import { parseTelegramLinks } from "./telegramLink"
import { useMassOpsRunner } from "./useMassOpsRunner"

export interface RoundRobinAssignment {
  link: string
  clientId: string
  clientName: string
}

/** Round-robin: ссылка i → клиент i mod N. Чистая функция. */
export function distributeRoundRobin(
  links: string[],
  clients: Pick<Userbot.Client, "id" | "name">[],
): RoundRobinAssignment[] {
  if (clients.length === 0) return []
  return links.map((link, i) => {
    const client = clients[i % clients.length]
    return { link, clientId: client.id, clientName: client.name }
  })
}

type TargetMode = "role" | "clients"

const StatusChip = ({ item }: { item: MassOpItem }) => {
  switch (item.status) {
    case "waiting":
      return <Chip size="small" label="Ожидает" />
    case "launching":
      return (
        <Chip
          size="small"
          color="info"
          label="Запуск"
          icon={<CircularProgress size={12} color="inherit" />}
        />
      )
    case "done":
      return <Chip size="small" color="success" label="Запущено" />
    case "error":
      return <Chip size="small" color="error" label="Ошибка" />
    default:
      return null
  }
}

export function JoinTab() {
  const snapshot = useMassOpsRunner()
  const [lazyClients] = useLazyClientsQuery()
  const clientsList = useInfiniteSelectList<Userbot.Client>({
    fetchPage: (p) => lazyClients(p).unwrap(),
    getId: (c) => c.id,
  })
  const [lazyRoles] = useLazyRolesQuery()
  const rolesList = useInfiniteSelectList<Userbot.Role>({
    fetchPage: (p) => lazyRoles(p).unwrap(),
    getId: (r) => r.name,
  })

  const [linksText, setLinksText] = useState("")
  const [mode, setMode] = useState<TargetMode>("clients")
  const [role, setRole] = useState("")
  const [selectedClients, setSelectedClients] = useState<Userbot.Client[]>([])

  const { links, discardedCount } = useMemo(
    () => parseTelegramLinks(linksText),
    [linksText],
  )

  const batchItems = snapshot.items.filter((i) => i.batchKind === "join")
  const batchStarted = batchItems.length > 0

  const clientNameById = useMemo(
    () => new Map(clientsList.items.map((c) => [c.id, c.name])),
    [clientsList.items],
  )

  // Клиенты — multiple с checkbox renderOption (element-form, несовместим
  // с array-form core ListboxComponent), поэтому без виртуализации:
  // догрузка следующей страницы только по скроллу (onScroll в ListboxProps).
  const onClientsScroll = (e: React.UIEvent<HTMLUListElement>) => {
    if (!clientsList.hasMore || clientsList.isFetching) return
    const el = e.currentTarget
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= 48)
      clientsList.loadMore()
  }

  const executorLabel = (item: MassOpItem) =>
    item.role ?? clientNameById.get(item.clientId!) ?? item.clientId ?? ""

  const canStart =
    links.length > 0 &&
    !snapshot.hasActive &&
    (mode === "role" ? role.trim() !== "" : selectedClients.length > 0)

  const handleStart = () => {
    const pairs =
      mode === "role"
        ? links.map((link) => ({ link, role: role.trim() }))
        : distributeRoundRobin(links, selectedClients).map((a) => ({
            link: a.link,
            clientId: a.clientId,
          }))
    massOpsRunner.startJoinBatch(pairs)
  }

  if (batchStarted) {
    return (
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2">
            Обработано: {snapshot.completed + snapshot.failed} /{" "}
            {snapshot.total}
          </Typography>
          <Button
            size="small"
            onClick={() => massOpsRunner.retryFailed()}
            disabled={snapshot.hasActive || snapshot.failed === 0}
          >
            Повторить упавшие
          </Button>
          <Button
            size="small"
            onClick={() => massOpsRunner.clearFinished()}
            disabled={snapshot.hasActive}
          >
            Очистить завершённые
          </Button>
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Ссылка</TableCell>
              <TableCell>Исполнитель</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Ошибка</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {batchItems.map((item) => (
              <TableRow key={item.key}>
                <TableCell>{item.link}</TableCell>
                <TableCell>{executorLabel(item)}</TableCell>
                <TableCell>
                  <StatusChip item={item} />
                </TableCell>
                <TableCell>
                  {item.error && (
                    <Typography variant="caption" color="error">
                      {item.error}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Stack>
    )
  }

  const previewRows =
    mode === "role"
      ? links.map((link) => ({ link, label: role.trim() || "—" }))
      : distributeRoundRobin(links, selectedClients).map((a) => ({
          link: a.link,
          label: a.clientName,
        }))

  return (
    <Stack spacing={2}>
      <TextField
        label="Ссылки на группы и каналы (по одной в строке)"
        multiline
        minRows={6}
        value={linksText}
        onChange={(e) => setLinksText(e.target.value)}
        helperText={
          discardedCount > 0
            ? `Отброшено строк: ${discardedCount} (пустые, дубликаты, невалидные)`
            : "Юзернейм, @юзернейм, t.me/ссылка или инвайт — по одной в строке"
        }
      />

      <RadioGroup
        row
        value={mode}
        onChange={(_, v) => setMode(v as TargetMode)}
      >
        <FormControlLabel
          value="clients"
          control={<Radio />}
          label="Клиенты (по кругу)"
        />
        <FormControlLabel value="role" control={<Radio />} label="Роль" />
      </RadioGroup>

      {mode === "clients" ? (
        <Autocomplete<Userbot.Client, true>
          multiple
          options={clientsList.items}
          value={selectedClients}
          onChange={(_, v) => setSelectedClients(v)}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          disableCloseOnSelect
          filterOptions={(o) => o}
          onInputChange={(_, v, reason) => {
            if (reason === "input") clientsList.setSearch(v)
          }}
          ListboxProps={{ onScroll: onClientsScroll }}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
              {option.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Клиенты"
              placeholder="Выберите клиентов"
            />
          )}
        />
      ) : (
        <Autocomplete<string, false, false, true>
          freeSolo
          options={rolesList.items.map((r) => r.name)}
          value={role}
          onInputChange={(_, v, reason) => {
            setRole(v)
            if (reason === "input") rolesList.setSearch(v)
          }}
          filterOptions={(o) => o}
          renderInput={(params) => <TextField {...params} label="Роль" />}
          {...infiniteListboxProps({
            hasMore: rolesList.hasMore,
            isFetching: rolesList.isFetching,
            onLoadMore: rolesList.loadMore,
          })}
          renderOption={(props, o, state) =>
            [props, o, state] as React.ReactNode
          }
        />
      )}

      {links.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Ссылка</TableCell>
              <TableCell>Исполнитель</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {previewRows.map((row) => (
              <TableRow key={row.link}>
                <TableCell>{row.link}</TableCell>
                <TableCell>{row.label}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Button
        variant="contained"
        onClick={handleStart}
        disabled={!canStart}
        sx={{ alignSelf: "flex-start" }}
      >
        Запустить ({links.length})
      </Button>
    </Stack>
  )
}
